import { Task } from '@shared/models/task.model';
import { Component, ChangeDetectorRef } from '@angular/core';
import { SubtasksService } from '@shared/modules/subtasks/subtasks.service';
import { TaskSidenavService } from '@shared/modules/task-sidenav/services/task-sidenav.service';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TaskStatus } from '@shared/enums/task-status.enum';
import { TaskType } from '@shared/enums/task-type.enum';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SnackBarService } from '@core/services/snackbar.service';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { Config } from '@shared/configs/config';
import { ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { RemoveModalController } from '@shared/controllers/remove-modal.controller';
import { TaskController } from '@shared/controllers/task.controller';
import { ListTaskEventType, ListTaskService } from '@shared/modules/list/services/list-task.service';

@Component({
  selector: 'subtasks-list',
  templateUrl: './subtasks-list.component.html',
  styleUrls: ['./subtasks-list.component.scss']
})
export class SubtasksListComponent {
  protected readonly TaskType = TaskType;

  constructor(
    public service: SubtasksService,
    public taskSidenavService: TaskSidenavService,
    private dialog: MatDialog,
    private t: TranslateService,
    private s: SnackBarService,
    private changes: ChangeDetectorRef,
    private listTaskService: ListTaskService
  ) {}

  taskList$: Observable<Task[]> = combineLatest([
    this.service.creationSubtasksList$,
    this.service.taskList$
  ]).pipe(
    map(([temp, saved]) => {
      const persistedTasks = (saved?.length ? saved : this.service.parentTask?.children || []).filter(Boolean);
      return [...temp, ...persistedTasks];
    })
  );

  compactMessageLikeLayout$: Observable<boolean> = this.taskList$.pipe(
    map((tasks) => {
      const visibleTasks = (tasks || []).filter((task) => !task?.deleted);
      if (!visibleTasks.length) return false;

      return visibleTasks.every((task) => {
        const type = String(task?.type || '');
        return type === TaskType.TYPE_MESSAGE || type === TaskType.TYPE_FOLLOW_UP;
      });
    })
  );

  trackBy(index: number, model: Task): number {
    return model.id;
  }

  confirmRemoveTask(task: Task): void {
    if (this.taskSidenavService.hasProcessTaskContext) {
      this.dialog
        .open(ConfirmModalComponent, {
          width: Config.CONFIRM_MODAL_WIDTH,
          autoFocus: false,
          data: {
            title: 'ProjectsDashboard.stageTasks.processTaskDeleteConfirmTitle',
            titleIcon: '/assets/img/ic-alert-circle.svg',
            body: 'ProjectsDashboard.stageTasks.processTaskDeleteConfirmBody',
            cancelButtonText: 'RemoveData.cancel',
            submitButtonText: 'RemoveData.confirm',
            submitButtonType: ButtonTypes.NEW_RED
          }
        })
        .afterClosed()
        .subscribe((confirmed: boolean | undefined) => {
          if (confirmed) {
            this.removeTask(task);
          }
        });
      return;
    }

    const ctrl = new RemoveModalController();
    ctrl.remove(() => this.removeTask(task)).subscribe();
  }

  private removeTask(task: Task): void {
    const ctrl = new TaskController(task);
    ctrl.removeTask(task).subscribe({
      next: () => this.successRemovingTask(task),
      error: () => this.errorRemovingTask()
    });
  }

  onTaskStatusChanged({
    task,
    applyStatusToSubtasks,
    subtasksCompleted
  }: {
    task: Task;
    subtasksCompleted?: boolean;
    applyStatusToSubtasks?: boolean;
  }): void {
    const updatedTask = new Task(task);
    const ctrl = new TaskController(updatedTask);
    const syncSubtasks$ = this.syncSubtasksStatus(
      updatedTask,
      applyStatusToSubtasks ?? subtasksCompleted
    );

    syncSubtasks$.subscribe({
      next: () => {
        ctrl.changeTaskStatus(updatedTask).subscribe({
          next: () => this.successStatusUpdate(updatedTask),
          error: () => this.errorStatusUpdate()
        });
      },
      error: () => this.errorStatusUpdate()
    });
  }

  getTaskStatus(task: Task): 'created' | 'completed' | 'cancelled' {
    if (task?.status === TaskStatus.COMPLETED) return 'completed';
    if (task?.status === TaskStatus.CANCELLED) return 'cancelled';
    return 'created';
  }

  private successRemovingTask(task: Task): void {
    this.service.setTaskList(this.service.taskListSnapshot.filter((item) => item?.id !== task.id));

    if (this.service.parentTask?.children) {
      this.service.parentTask.children = this.service.parentTask.children.filter((item) => item?.id !== task.id);
      this.service.parentTask.childrenCount = this.service.parentTask.children.filter(
        (item) => !item?.deleted
      ).length;
      this.service.parentTask.completedChildrenCount = this.service.parentTask.children.filter(
        (item) => item?.status === TaskStatus.COMPLETED && !item?.deleted
      ).length;
    }

    if (this.taskSidenavService.task?.children) {
      this.taskSidenavService.task.children = this.taskSidenavService.task.children.filter(
        (item) => item?.id !== task.id
      );
      this.taskSidenavService.task.childrenCount = this.taskSidenavService.task.children.filter(
        (item) => !item?.deleted
      ).length;
      this.taskSidenavService.task.completedChildrenCount = this.taskSidenavService.task.children.filter(
        (item) => item?.status === TaskStatus.COMPLETED && !item?.deleted
      ).length;
    }

    if (this.taskSidenavService.task?.id) {
      this.listTaskService.emitter.emit({
        type: ListTaskEventType.UPDATE_TASK,
        data: { task: this.taskSidenavService.task }
      });
    }

    this.listTaskService.emitter.emit({
      type: ListTaskEventType.REMOVE_TASK,
      data: { task, closeSidenav: false }
    });

    this.s.success(this.t.instant(`Tasks.${task?.type}Deleted`));
    this.changes.detectChanges();
  }

  private successStatusUpdate(task: Task): void {
    this.service.setTaskList(
      this.service.taskListSnapshot.map((item) => (item?.id === task.id ? new Task(task) : item))
    );

    if (this.service.parentTask?.children) {
      this.service.parentTask.children = this.service.parentTask.children.map((item) =>
        item?.id === task.id ? new Task(task) : item
      );
    }

    if (this.taskSidenavService.task?.children) {
      this.taskSidenavService.task.children = this.taskSidenavService.task.children.map((item) =>
        item?.id === task.id ? new Task(task) : item
      );
    }

    if (this.taskSidenavService.task?.id === task.id) {
      this.taskSidenavService.task = new Task(task);
    }

    this.listTaskService.emitter.emit({ type: ListTaskEventType.UPDATE_COUNTERS });
    this.listTaskService.emitter.emit({ type: ListTaskEventType.UPDATE_TASK, data: { task } });
    this.changes.detectChanges();
  }

  private errorRemovingTask(): void {
    this.s.error(this.t.instant('Tasks.errorDelete'));
  }

  private errorStatusUpdate(): void {
    this.s.error(this.t.instant('Tasks.errorTaskUpdate'));
  }

  private syncSubtasksStatus(task: Task, applyStatusToSubtasks?: boolean) {
    if (!applyStatusToSubtasks) return of(null);

    if (task?.status === TaskStatus.COMPLETED) {
      return new TaskController(task).completeSubtasks(task);
    }

    const subtasks = this.collectDescendantTasks(task);
    if (!subtasks.length) return of(null);

    return forkJoin(
      subtasks.map((subtask) => {
        const ctrl = new TaskController(subtask);
        return ctrl.changeTaskStatus(subtask);
      })
    );
  }

  private collectDescendantTasks(task: Task): Task[] {
    const descendants: Task[] = [];

    const collect = (parent: Task): void => {
      (parent?.children || []).forEach((child) => {
        descendants.push(child);
        collect(child);
      });
    };

    collect(task);
    return descendants;
  }
}
